import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Container, CircularProgress, Alert, Box } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { categoryService, statsService } from "../api/services";
import AdminLayout from "../components/Layout/AdminLayout";

// Importar las secciones - ASEGÚRATE QUE ESTAS RUTAS SEAN CORRECTAS
import IAEditorSection from "../components/AdminSections/IAEditorSection";
import AutoGenerationConfig from "../components/AutoGenerationConfig";
import SchedulingSection from "../components/AdminSections/SchedulingSection";
import CategoryManagementSection from "../components/AdminSections/CategoryManagementSection";
import IADraftsSection from "../components/AdminSections/IADraftsSection";
import AnalyticsSection from "../components/AdminSections/AnalyticsSection";
import UserManagementSection from "../components/AdminSections/UserManagementSection";
import AdminDashboard from "../components/AdminSections/AdminDashboard"; // ✅ IMPORTACIÓN CORRECTA
import PublishedPostsSection from "../components/AdminSections/PublishedPostsSection";
import BlogList from "../components/Blog/BlogList";

import AllPostsSection from "../components/AdminSections/AllPostsSection";

const QuickActions = ({ onSectionChange, activeSection }) => {
  const actions = [
    { key: "dashboard", label: "Dashboard", icon: null },
    { key: "ia", label: "Generación IA", icon: null },
    { key: "auto-generation", label: "Auto-Generación", icon: null },
    { key: "scheduling", label: "Programación", icon: null },
    { key: "categories", label: "Categorías", icon: null },
    { key: "drafts", label: "Borradores IA", icon: null },
    { key: "analytics", label: "Analytics", icon: null },
    { key: "users", label: "Usuarios", icon: null },
    { key: "all-posts", label: "Todos los Posts", icon: null },
  ];

  return (
    <Box sx={{ mb: 3, overflowX: 'auto', whiteSpace: 'nowrap', pb: 1 }}>
      {actions.map((action) => (
        <button
          key={action.key}
          onClick={() => onSectionChange(action.key)}
          style={{
            marginRight: "10px",
            backgroundColor: activeSection === action.key ? "#8A2BE2" : "white",
            color: activeSection === action.key ? "white" : "black",
            border: "1px solid #ccc",
            padding: "5px 10px",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          {action.label}
        </button>
      ))}
    </Box>
  );
};

function Admin() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const categoriesResponse = await categoryService.getCategories();
      setCategories(
        Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []
      );

      try {
        const statsResponse = await statsService.getStats();
        setStats(statsResponse.data);
      } catch (statsError) {
        setStats([]);
      }
    } catch (error) {
      console.error("❌ Error in fetchInitialData:", error);
      setCategories([]);
      setStats([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.profile?.role === "admin") {
      fetchInitialData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user, fetchInitialData]);

  if (!isAuthenticated || user?.profile?.role !== "admin") {
    return (
      <AdminLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">
            No tienes permisos para acceder al panel de administración.
          </Alert>
        </Container>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  // Determinar la sección activa basada en la URL
  const getActiveSection = () => {
    const path = location.pathname.replace("/admin/", "");
    if (!path) return "dashboard";
    return path;
  };

  const activeSection = getActiveSection();

  return (
    <AdminLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <QuickActions
          onSectionChange={(section) => {
            window.location.href = `/admin/${
              section === "dashboard" ? "" : section
            }`;
          }}
          activeSection={activeSection}
        />

        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="ia" element={<IAEditorSection />} />
          <Route path="auto-generation" element={<AutoGenerationConfig categories={categories} />} />
          <Route path="scheduling" element={<SchedulingSection />} />
          <Route path="categories" element={<CategoryManagementSection />} />
          <Route path="drafts" element={<IADraftsSection />} />
          <Route path="analytics" element={<AnalyticsSection />} />
          <Route path="users" element={<UserManagementSection />} />
          <Route path="published" element={<PublishedPostsSection />} />
          <Route path="all-posts" element={<AllPostsSection />} />
        </Routes>
      </Container>
    </AdminLayout>
  );
}

export default Admin;
